import React, { useState } from 'react';
import TestSetup from './TestSetup';
import ImmersiveTest from './ImmersiveTest';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const TestContainer = ({ defaultTopic = '', onExit }) => {
  const [testState, setTestState] = useState('setup'); // setup, loading, testing, complete
  const [testConfig, setTestConfig] = useState(null);
  const [testData, setTestData] = useState(null);
  const [error, setError] = useState(null);

  const handleStartTest = async (config) => {
    setTestState('loading');
    setTestConfig(config);
    setError(null);

    try {
      // Call backend to generate test
      const response = await fetch(`${API_BASE}/api/generate_test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic: config.topic,
          question_count: config.questionCount,
          test_type: config.testType,
          difficulty: config.difficulty,
          time_limit: config.timeLimit,
          show_feedback: config.showFeedback,
          random_order: config.randomOrder
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate test');
      }

      const data = await response.json();
      
      // Format test data
      const formattedTest = {
        title: data.title || `${config.topic} Test`,
        questions: data.questions || []
      };

      setTestData(formattedTest);
      setTestState('testing');
    } catch (err) {
      console.error('Error generating test:', err);
      setError(err.message || 'Failed to generate test. Please try again.');
      setTestState('setup');
    }
  };

  const handleTestComplete = (results) => {
    console.log('Test completed with results:', results);
    // You can send results to backend or parent component here
  };

  const handleCancel = () => {
    setTestState('setup');
    setTestData(null);
    setTestConfig(null);
    if (onExit) onExit();
  };

  // Loading State
  if (testState === 'loading') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-primary, #0a0a0f)',
        color: '#fafafa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(59, 130, 246, 0.2)',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ fontSize: '1.125rem', fontWeight: 600 }}>
            Generating your test...
          </p>
          <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.5)' }}>
            Creating {testConfig?.questionCount} questions
          </p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-primary, #0a0a0f)',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '32px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#ef4444', marginBottom: '12px' }}>Error</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '24px' }}>
            {error}
          </p>
          <button
            onClick={() => {
              setError(null);
              setTestState('setup');
            }}
            style={{
              padding: '12px 24px',
              background: '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Testing State
  if (testState === 'testing' && testData && testConfig) {
    return (
      <ImmersiveTest
        test={testData}
        config={testConfig}
        onComplete={handleTestComplete}
        onExit={handleCancel}
      />
    );
  }

  // Setup State (default)
  return (
    <TestSetup
      defaultTopic={defaultTopic}
      onStart={handleStartTest}
      onCancel={onExit}
    />
  );
};

export default TestContainer;
